// Standalone CSV upload + validate + correct hook for the Contacts page.
//
// The parse / validate / correction flow is the same one the pre-sales-
// platform Run Wizard used (see `pages/NewRunPage/useNewRunPage.ts`):
// parseCSV -> setLeads + setValidationErrors -> user fixes inline via
// ErrorRowEditor / ErrorEditorModal -> handleSaveCorrections re-validates.
// Copying the logic (rather than importing from useNewRunPage) keeps this
// hook independent of the wizard's 900-line useNewRunPage state bundle.

import { useCallback, useRef, useState } from 'react';
import {
  validateEmail,
  validatePhoneNumber,
  validateRequiredField,
} from '../NewRunPage/newRunValidation';
import type { Lead, ValidationError } from '../NewRunPage/newRunTypes';
import { CSV_SAMPLE_ROWS } from '../../config/region';

export interface CsvUploadToast {
  type: 'success' | 'error';
  message: string;
}

export interface UseCsvLeadsUpload {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  selectedFile: File | null;
  leads: Lead[];
  validationErrors: ValidationError[];
  previewMode: boolean;
  toast: CsvUploadToast | null;
  showTemplatePopup: boolean;
  setPreviewMode: (v: boolean) => void;
  setShowTemplatePopup: (v: boolean) => void;
  setToast: (t: CsvUploadToast | null) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleClearFile: () => void;
  handleSaveCorrections: (correctedLeads: Lead[]) => void;
  exportValidationErrors: () => void;
  downloadTemplate: () => void;
  reset: () => void;
}

export function useCsvLeadsUpload(): UseCsvLeadsUpload {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);
  const [toast, setToast] = useState<CsvUploadToast | null>(null);

  const parseCSV = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter((line) => line.trim());

      if (lines.length < 2) {
        setToast({
          type: 'error',
          message: 'CSV file must contain at least a header row and one data row',
        });
        setLeads([]);
        setValidationErrors([]);
        return;
      }

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());

      if (lines.length - 1 > 1000) {
        setToast({ type: 'error', message: 'CSV cannot contain more than 1000 rows' });
        setLeads([]);
        setValidationErrors([]);
        return;
      }

      if (!headers.includes('name') || !headers.includes('phone')) {
        setToast({ type: 'error', message: 'CSV must contain "name" and "phone" columns' });
        setLeads([]);
        setValidationErrors([]);
        return;
      }

      const parsedLeads: Lead[] = [];
      const errors: ValidationError[] = [];
      const phoneNumbers = new Set<string>();

      for (let i = 1; i < lines.length; i++) {
        const rowNumber = i + 1;
        const values = lines[i].split(',').map((v) => v.trim());

        if (values.length < headers.length) {
          errors.push({
            row: rowNumber,
            field: 'general',
            message: `Row has ${values.length} values but ${headers.length} columns are expected`,
          });
          continue;
        }

        const name = values[headers.indexOf('name')] || '';
        const phone = values[headers.indexOf('phone')] || '';
        const email = headers.includes('email') ? values[headers.indexOf('email')] || '' : '';

        let normalizedPhone = phone;

        if (!validateRequiredField(name, 'name')) {
          errors.push({ row: rowNumber, field: 'name', message: 'Name is required and cannot be empty' });
        }

        if (!validateRequiredField(phone, 'phone')) {
          errors.push({ row: rowNumber, field: 'phone', message: 'Phone number is required and cannot be empty' });
        } else {
          const phoneValidation = validatePhoneNumber(phone);
          if (!phoneValidation.isValid) {
            errors.push({
              row: rowNumber,
              field: 'phone',
              message: phoneValidation.error || 'Invalid phone number format',
            });
          } else if (phoneValidation.normalized) {
            if (phoneNumbers.has(phoneValidation.normalized)) {
              errors.push({
                row: rowNumber,
                field: 'phone',
                message: `Duplicate phone number: ${phoneValidation.normalized}`,
              });
            } else {
              phoneNumbers.add(phoneValidation.normalized);
              normalizedPhone = phoneValidation.normalized;
            }
          }
        }

        if (email && !validateEmail(email)) {
          errors.push({
            row: rowNumber,
            field: 'email',
            message: 'Email must be in a valid format (e.g., user@example.com)',
          });
        }

        parsedLeads.push({ name, phone: normalizedPhone, email });
      }

      setLeads(parsedLeads);
      setValidationErrors(errors);
      if (errors.length > 0) {
        setToast({
          type: 'error',
          message: `Found ${errors.length} validation error(s) in your CSV file. Fix them below to continue.`,
        });
      }
    };
    reader.readAsText(file);
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      setSelectedFile(file);
      setValidationErrors([]);
      parseCSV(file);
    } else {
      setToast({ type: 'error', message: 'Please select a valid CSV file' });
      setValidationErrors([]);
    }
  }, [parseCSV]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      setSelectedFile(file);
      setValidationErrors([]);
      parseCSV(file);
    } else {
      setToast({ type: 'error', message: 'Please drop a valid CSV file' });
      setValidationErrors([]);
    }
  }, [parseCSV]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setLeads([]);
    setValidationErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const reset = handleClearFile;

  const handleSaveCorrections = useCallback((correctedLeads: Lead[]) => {
    const errors: ValidationError[] = [];
    const phoneNumbers = new Set<string>();

    correctedLeads.forEach((lead, index) => {
      const row = index + 2;

      if (!lead.name || !lead.name.trim()) {
        errors.push({ row, field: 'name', message: 'Name is required' });
      }

      if (!lead.phone || !lead.phone.trim()) {
        errors.push({ row, field: 'phone', message: 'Phone number is required' });
      } else {
        const phoneValidation = validatePhoneNumber(lead.phone);
        if (!phoneValidation.isValid) {
          errors.push({
            row,
            field: 'phone',
            message: phoneValidation.error || 'Invalid phone number format',
          });
        } else if (phoneValidation.normalized) {
          if (phoneNumbers.has(phoneValidation.normalized)) {
            errors.push({ row, field: 'phone', message: 'Duplicate phone number in CSV' });
          } else {
            phoneNumbers.add(phoneValidation.normalized);
            correctedLeads[index].phone = phoneValidation.normalized;
          }
        }
      }

      if (lead.email && lead.email.trim() && !validateEmail(lead.email)) {
        errors.push({
          row,
          field: 'email',
          message: 'Email must be in a valid format (e.g., user@example.com)',
        });
      }
    });

    setLeads(correctedLeads);
    setValidationErrors(errors);

    if (errors.length === 0) {
      setToast({ type: 'success', message: 'All errors fixed! Your contacts are ready to upload.' });
    } else {
      setToast({
        type: 'error',
        message: `${errors.length} ${errors.length === 1 ? 'error remains' : 'errors remain'}. Continue fixing to proceed.`,
      });
    }
  }, []);

  const exportValidationErrors = useCallback(() => {
    if (validationErrors.length === 0) return;
    const csvHeader = 'Row,Field,Error Message\n';
    const csvContent = validationErrors
      .map((error) => `${error.row},"${error.field}","${error.message.replace(/"/g, '""')}"`)
      .join('\n');
    const csvData = csvHeader + csvContent;
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `validation_errors_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [validationErrors]);

  const downloadTemplate = useCallback(() => {
    const templateHeader = 'name,phone,email\n';
    const templateData = CSV_SAMPLE_ROWS.join('\n');
    const csvData = templateHeader + templateData;
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'contacts_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowTemplatePopup(false);
  }, []);

  return {
    fileInputRef,
    selectedFile,
    leads,
    validationErrors,
    previewMode,
    toast,
    showTemplatePopup,
    setPreviewMode,
    setShowTemplatePopup,
    setToast,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleClearFile,
    handleSaveCorrections,
    exportValidationErrors,
    downloadTemplate,
    reset,
  };
}
